import { EditRounded, Search } from "@mui/icons-material";
import {
  InputAdornment,
  Paper,
  Select,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";

import {
  GridColDef,
  GridActionsCellItem,
  DataGrid,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import "./styles.scss";
import { ClientType } from "../../types/genericType";
import { formatPhoneNumber } from "../../utils/formatPhoneNumber";

type ClientsTableProps = {
  data: Array<ClientType>;
  selectedClient: ClientType;
  updateSelectedClient: Function;
  setOpenEditDrawer: Function;
};

const ClientsTable = (props: ClientsTableProps) => {
  const { data, updateSelectedClient, setOpenEditDrawer } = props;
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredRows, setFilteredRows] = useState(data);
  const [seletedStatus, setSelectedStatus] = useState("active");

  const handleSearch = (event: any) => {
    const term = event.target.value;
    setSearchTerm(term);
  };

  useEffect(() => {
    const termParts = searchTerm.trim().toLowerCase().split(/\s+/);
    const filteredData = data.filter((row) => {
      const matchedTerm = termParts.every((term) =>
        Object.values(row).some(
          (value) =>
            typeof value === "string" && value.toLowerCase().includes(term)
        )
      );

      const matchesStatus =  seletedStatus === '*' || (row.isActive ? "active" : "inactive") === seletedStatus;
      return matchedTerm && matchesStatus;
    });

    setFilteredRows(filteredData);
  }, [data, searchTerm, seletedStatus]);

  const maskTaxNumber = (taxNumber: string) => {
    if (taxNumber) {
      const visibleLength = 4;
      const maskedCharacters = taxNumber.length - visibleLength;

      if (maskedCharacters <= 0) {
        return taxNumber;
      }

      const maskedPart = taxNumber.slice(0, -visibleLength).replace(/./g, "*");
      const visiblePart = taxNumber.slice(-visibleLength);

      return maskedPart + visiblePart;
    }
  };

  const columns: GridColDef[] = React.useMemo<GridColDef[]>(
    () => [
      {
        field: "name",
        headerName: "Name",
        hideable: false,
        minWidth: 290,
        headerClassName: "header-datagrid",
        valueGetter: (params) => {
          return `${
            params.row.companyName
              ? params.row.companyName
              : params.row.firstName + " " + params.row.lastName
          } `;
        },
      },
      {
        field: "address",
        headerName: "Address",
        hideable: false,
        minWidth: 340,
        sortable: false,
        headerClassName: "header-datagrid",
        valueGetter: (params) => {
          return `${params.row.address ? `${params.row.address},` : ""}  ${
            params.row.apartment ? `${params.row.apartment},` : ""
          } ${params.row.city ? `${params.row.city},` : ""} ${
            params.row.state ? `${params.row.state}` : ""
          }`;
        },
      },
      {
        field: "taxNumber",
        headerName: "TIN",
        hideable: false,
        minWidth: 120,
        sortable: false,
        headerClassName: "header-datagrid",
        renderCell: (params) => {
          return <span>{maskTaxNumber(params.value)}</span>;
        },
      },
      {
        field: "phone",
        headerName: "Phone",
        hideable: false,
        minWidth: 180,
        sortable: false,
        headerClassName: "header-datagrid",
        valueGetter: (params) => {
          return formatPhoneNumber(
            params?.row?.mobile ||
              params?.row?.officePhone ||
              params?.row?.homePhone ||
              params?.row?.fax
          );
        },
      },
      {
        field: "status",
        headerName: "Status",
        hideable: false,
        minWidth: 140,
        headerClassName: "header-datagrid",
        valueGetter: (params) => {
          return params.row.isActive ? "Active" : "Inactive";
        },
      },
      {
        field: "type",
        headerName: "Type",
        hideable: false,
        minWidth: 140,
        sortable: false,
        headerClassName: "header-datagrid",
      },

      {
        field: "id",
        headerName: "Edit",
        sortable: false,
        width: 30,
        align: "center",
        headerAlign: "center",
        headerClassName: "header-datagrid",
        type: "actions",
        getActions: (params: GridValueGetterParams) => [
          <GridActionsCellItem
            icon={<EditRounded sx={{ fontSize: "20px" }} />}
            label=""
            onClick={() => {
              setOpenEditDrawer(true);
              updateSelectedClient(params.row);
            }}
          />,
        ],
      },
    ],
    []
  );

  return (
    <Paper elevation={3} className="users-table">
      <Stack spacing={2} direction="row" className="search-filter" padding="20px">
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          className="search-field"
          placeholder="Search..."
          value={searchTerm}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />

        <FormControl style={{width:200}}>
          <InputLabel id="status_label" shrink>Status</InputLabel>

          <Select
            labelId="status_label"
            label="Status"
            value={seletedStatus}
            onChange={(event: SelectChangeEvent) =>
              setSelectedStatus(event.target.value as string)
            }
            sx={{
              height: '40px', // Adjust this value as needed
              padding: '0 14px', // Adjust this value as needed
              '& .MuiSelect-select': {
                padding: '0px 0', // Adjust this value as needed
              }
            }} 
            
             
          >
            <MenuItem value="*">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>
      </Stack>
      {filteredRows.length > 0 ? (
        <DataGrid
          rows={filteredRows}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          pageSizeOptions={[10, 20, 30, 40, 50]}
          rowSelection={false}
          className="datagrid"
        />
      ) : (
        <div className="no-results">No results found</div>
      )}
    </Paper>
  );
};

export default ClientsTable;
